import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { MdOutlineRadioButtonChecked, MdOutlineRadioButtonUnchecked } from 'react-icons/md'
import { LinearProgress } from '@material-ui/core'
import { useRepo } from 'querys/github'
import { Typography, P } from 'common/typography'
import { TransparentButton as TB } from 'common/button'
import Stack from 'student/projects/stack'
import { UpdateRepo, FieldTitle, UpdateField } from './repositories-status-compontents'

const TransparentButton = styled(TB)`
  font: var(--p2Regular);
  color: var(--secondary);
  display: block;
  margin-bottom: 16px;
  font-weight: 600;
  @media(min-width: 768px) {
    font: var(--regular);
    font-weight: 600;
  }
`

const ContainerStyled = styled.div`
  margin-top: 8px;
  min-height: 40vh;
  .loading-status {
    color: var(--secondary);
  }
  .repo-status-message {
    position: absolute;
  }
  .MuiLinearProgress-root {
    background: var(--primaryDisabled);
  }
  .MuiLinearProgress-bar {
    background-color: var(--primary);
  }
  @media(min-height: 768px) {
    min-height: initial;
  }
`

const RepositoryStatusStyled = styled.div`
  max-width: calc(100vw - 64px);
  box-sizing: border-box;
  border-radius: 4px;
  background: var(--white);
  color: var(--neutral100);
  position: relative;
  padding: 0;
  a {
    color: var(--secondary);
    transition: 200ms ease-in-out color;
    overflow-wrap: break-word;
    &:hover {
      color: #DB086F;
      text-decoration: underline;
    }
    &:active {
      color: #AA0351;
    }
  }
  p {
    margin: 8px 0 16px;
  }
  .repo-description p {
    overflow: hidden;
    display: -webkit-box;
    margin-bottom: 0px;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
  }
  .level {
    display: flex;
    gap: .5rem;
    input {
      display: none;
    }
    .checked {
      display: none;
    }
    input:checked ~ .checked {
      display: block;
    }
    input:checked ~ .unchecked {
      display: none;
    }
  }
  .level-container {
    display: flex;
    gap: 2.5rem;
  }
  @media(min-width: 768px) {
    padding: 16px;
    max-block-size: 328px;
    overflow-y: auto;
  }
`

function LevelSelect({ id, option, criteria, setCriteria }) {
  return (
    <label htmlFor={id} className="level">
      <input id={id} checked={id === criteria} className="radio-btn" name="requirements_level" type="radio" value={id} onClick={() => setCriteria(id)} />
      {criteria === id ? (
        <MdOutlineRadioButtonChecked size="16" color="var(--turquoise)" />
      ) : (
        <MdOutlineRadioButtonUnchecked size="16" color="var(--turquoise)" />
      )}
      <span>{option}</span>
    </label>
  )
}

function RepositoryStatus({
  selected, setDisabled, setSelected, closeRepo, requirements,
}) {
  const [criteria, setCriteria] = useState('junior')
  const { data, isLoading } = useRepo(selected.name)

  useEffect(() => {
    if (data && data.topics.length > 0 && data.html_url && data.homepage && data.description) {
      setSelected(data)
      setDisabled(false)
    } else {
      setDisabled(true)
    }
  }, [data])

  if (isLoading) {
    return (
      <ContainerStyled>
        <div className="loading-status">
          <LinearProgress />
        </div>
      </ContainerStyled>
    )
  }

  const {
    html_url, homepage, description, topics,
  } = data

  const isTotalyEmpty = !homepage && !description && !topics?.length

  return (
    <RepositoryStatusStyled className="repository-status">
      <TransparentButton className="change-repository" id="restart-form" onClick={() => closeRepo()}>
        Cambiar repositorio
      </TransparentButton>
      {isTotalyEmpty ? (
        <>
          <Typography small="regularBold" large="headline6" as="h5">
            ¡Atención!
          </Typography>
          <Typography as="p">
            Para poder enviar un proyecto a revisión es necesario que la sección de
            <b> About </b>
            en tu repositorio de GitHub este completa.
          </Typography>
          <Typography as="p">
            Al actualizar los datos en GitHub se verán reflejados aquí y podrás mandar a revisión tu proyecto.
          </Typography>
          <UpdateRepo repo={html_url} />
        </>
      ) : (
        <>
          {requirements?.junior ? (
            <>
              <FieldTitle title="Criterios de aceptación">
                <p className="tooltip-text">
                  Selecciona los requerimientos que deseas que evaluemos.
                </p>
              </FieldTitle>
              <div className="level-container">
                {requirements?.junior ? (
                  <LevelSelect id="junior" option="Junior" criteria={criteria} setCriteria={setCriteria} />
                ) : null}
                {requirements?.mid ? (
                  <LevelSelect id="mid" option="Mid" criteria={criteria} setCriteria={setCriteria} />
                ) : null}
                {requirements?.senior ? (
                  <LevelSelect id="senior" option="Senior" criteria={criteria} setCriteria={setCriteria} />
                ) : null}
              </div>
            </>
          ) : null}
          <FieldTitle title="Proyecto">
            <p className="tooltip-text">
              Es la URL de demo o Deploy de tu proyecto. Esto lo encontrarás como el campo
              <b> Website </b>
              a la hora de editar el About en GitHub.
            </p>
          </FieldTitle>
          {homepage ? (
            <div className="homepage-link">
              <a href={homepage} target="_blank" rel="noopener noreferrer">{homepage}</a>
            </div>
          ) : <UpdateField description="El repositorio carece de url de deploy" repo={html_url} />}
          <FieldTitle title="Stack">
            <p className="tooltip-text">
              Son las etiquetas de las tecnologías que utilizaste para desarrollar tu proyecto. Esto lo encontrarás como el campo
              <b> Topics </b>
              a la hora de editar el About en GitHub.
            </p>
          </FieldTitle>
          {topics.length !== 0 ? (
            <Stack stack={topics} />
          ) : <UpdateField description="El repositorio carece de etiquetas con tu stack." repo={html_url} />}
          <FieldTitle title="Descripción corta">
            <p className="tooltip-text">
              Es una reseña corta del valor y propósito del proyecto que estás desarrollando. Esto lo encontrarás como el campo
              <b> Description </b>
              a la hora de editar el About en GitHub.
            </p>
          </FieldTitle>
          <div className="repo-description">
            {description ? (
              <P>{description}</P>
            ) : <UpdateField description="El repositorio carece de una descripción." repo={html_url} />}
          </div>
        </>
      )}
    </RepositoryStatusStyled>
  )
}

export default RepositoryStatus
